import './floatingModal.css'
import { Modal, ModalBody, ModalContent, ModalHeader, ModalFooter, ModalOverlay, Flex, BoxProps } from '@chakra-ui/core'
import React, { useEffect, useMemo, useRef } from 'react'
import { createPortal } from 'react-dom'
import FocusLock from 'react-focus-lock'
import { hideOthers } from 'aria-hidden'
import { useScrollMemory } from 'hooks/useScrollMemory'

type DivProps = React.HTMLAttributes<HTMLDivElement>

export interface BaseModalV3Props {
  floating?: boolean
  isOpen: boolean
  onClose: () => void
  header: React.ReactNode
  body: React.ReactNode
  footer: React.ReactNode

  /**
   * If true, clicking outside the content closes the modal.
   * Default: false (same behavior as BaseModalV2).
   */
  closeOnOverlayClick?: boolean

  /**
   * If true, pressing Escape key closes the modal.
   * Default: false.
   */
  closeOnEsc?: boolean

  /**
   * Render into document.body via portal. Default: true.
   */
  usePortal?: boolean

  /** Keep keyboard focus in the dialog and restore its trigger on close. Opt-in. */
  trapFocus?: boolean

  /**
   * When set, the scroll body remembers/restores its scrollTop per this key
   * (via useScrollMemory). Pass the active content-frame key so navigating between
   * frames returns to each frame's own scroll position. Opt-in.
   */
  scrollMemoryKey?: string

  overlayProps?: DivProps
  contentProps?: BoxProps
  headerProps?: DivProps
  bodyProps?: BoxProps
  footerProps?: BoxProps
}

/**
 * BaseModalV3 — "vanilla" modal (div + CSS) to rule out Chakra Modal quirks.
 * - No Chakra components
 * - Body scroll area has `data-scroll-container` attribute (used elsewhere in this feature)
 * - Centers via flex (no transform translate)
 */
export const BaseModalV3: React.FC<BaseModalV3Props> = ({
  floating = false,
  isOpen,
  onClose,
  header,
  body,
  footer,
  closeOnOverlayClick = false,
  closeOnEsc = false,
  usePortal = true,
  trapFocus = false,
  scrollMemoryKey,
  overlayProps,
  contentProps,
  headerProps,
  bodyProps,
  footerProps,
}) => {
  const contentRef = useRef<HTMLDivElement | null>(null)
  const { setScrollRef, onScroll } = useScrollMemory(scrollMemoryKey)

  const styles = useMemo(() => {
    return {
      overlay: {
        position: 'fixed' as const,
        inset: 0,
        background: 'rgba(0, 0, 0, 0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1400,
      },
      content: {
        width: '70vw',
        maxWidth: 800,
        minWidth: 350,
        background: '#fff',
        borderRadius: 5,
        boxShadow: '0 7px 14px 0 rgba(0,0,0, 0.1), 0 3px 6px 0 rgba(0, 0, 0, .07)',
        display: 'flex',
        flexDirection: 'column' as const,
        overflow: 'hidden',
      },
      header: {
        padding: '10px 20px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        zIndex: 1,
        flexShrink: 0,
        background: '#fff',
      },
      body: {
        padding: '15px 25px',
        background: '#f7f7f7',
        overflowY: 'auto' as const,
        flex: '1 1 auto',
        maxHeight: '75vh',
        height: 'calc(100vh - 132px)',
        display: 'flex',
        flexDirection: 'column' as const,
        justifyContent: 'space-between',
        minHeight: 0,
        overscrollBehaviorY: 'contain' as const,
      },
      footer: {
        padding: '15px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'hidden',
        flexShrink: 0,
        background: '#F7F7F7',
      },
    }
  }, [])

  useEffect(() => {
    if (!isOpen) return

    const prevBodyOverflow = document.body.style.overflow
    const prevBodyPaddingRight = document.body.style.paddingRight
    const prevScrollableDivOverflow = (document.getElementById('scrollableDiv') as HTMLDivElement | null)?.style
      .overflow

    const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth
    document.body.style.overflow = 'hidden'
    if (scrollBarWidth > 0) document.body.style.paddingRight = `${scrollBarWidth}px`

    const scrollableDiv = document.getElementById('scrollableDiv') as HTMLDivElement | null
    if (scrollableDiv) scrollableDiv.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = prevBodyOverflow
      document.body.style.paddingRight = prevBodyPaddingRight
      if (scrollableDiv) scrollableDiv.style.overflow = prevScrollableDivOverflow ?? 'auto'
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen || !trapFocus || !contentRef.current) return
    return hideOthers(contentRef.current)
  }, [isOpen, trapFocus])

  useEffect(() => {
    if (!isOpen || !closeOnEsc) return

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented) {
        e.preventDefault()
        onClose()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [isOpen, onClose, closeOnEsc])

  if (!isOpen) return null
  if (typeof document === 'undefined') return null

  const handleOverlayMouseDown: React.MouseEventHandler<HTMLDivElement> = (e) => {
    overlayProps?.onMouseDown?.(e)
    if (!closeOnOverlayClick) return
    if (e.defaultPrevented) return
    if (e.target === e.currentTarget) onClose()
  }

  const node = (
    <div
      {...overlayProps}
      style={{ ...styles.overlay, ...(overlayProps?.style ?? {}) }}
      onMouseDown={handleOverlayMouseDown}
    >
      <div
        {...contentProps}
        ref={(el) => {
          contentRef.current = el
          const ref = (contentProps as any)?.ref
          if (typeof ref === 'function') ref(el)
        }}
        className={[contentProps?.className, floating ? 'floating-task-modal' : ''].filter(Boolean).join(' ')}
        role="dialog"
        aria-modal="true"
        tabIndex={trapFocus ? -1 : contentProps?.tabIndex}
        style={{ ...styles.content, ...(contentProps?.style ?? {}) }}
      >
        <div {...headerProps} data-modal-part="header" style={{ ...styles.header, ...(headerProps?.style ?? {}) }}>
          {header}
        </div>
        <div
          {...bodyProps}
          ref={scrollMemoryKey ? setScrollRef : (bodyProps as any)?.ref}
          onScroll={(e) => {
            ;(bodyProps as any)?.onScroll?.(e)
            if (scrollMemoryKey) onScroll()
          }}
          data-modal-part="body"
          data-scroll-container
          style={{ ...styles.body, ...(bodyProps?.style ?? {}) }}
        >
          {body}
        </div>
        <div {...footerProps} data-modal-part="footer" style={{ ...styles.footer, ...(footerProps?.style ?? {}) }}>
          {footer}
        </div>
      </div>
    </div>
  )

  // FocusLock follows React portal events, so nested menus can remain portaled to body.
  const modal = trapFocus ? <FocusLock returnFocus={{ preventScroll: true }}>{node}</FocusLock> : node
  return usePortal ? createPortal(modal, document.body) : modal
}

interface IProps {
  floating?: boolean
  isOpen: boolean
  onClose: () => void
  header: JSX.Element
  body: JSX.Element
  footer: JSX.Element
  /**
   * When set, the scroll body remembers/restores its scrollTop per this key
   * (via useScrollMemory). Pass the active content-frame key so navigating between
   * frames returns to each frame's own scroll position. Opt-in.
   */
  scrollMemoryKey?: string
  modalProps?: Partial<React.ComponentProps<typeof Modal>>
  modalOverlayProps?: Partial<React.ComponentProps<typeof ModalOverlay>>
  modalContentProps?: Partial<React.ComponentProps<typeof ModalContent>>
  modalHeaderProps?: Partial<React.ComponentProps<typeof ModalHeader>>
  modalBodyProps?: Partial<React.ComponentProps<typeof ModalBody>>
  modalFooterProps?: Partial<React.ComponentProps<typeof ModalFooter>>
}

const BaseModalV2: React.FC<IProps> = ({
  floating = false,
  isOpen,
  onClose,
  header,
  body,
  footer,
  scrollMemoryKey,
  modalProps,
  modalOverlayProps,
  modalContentProps,
  modalHeaderProps,
  modalBodyProps,
  modalFooterProps,
}) => {
  const { setScrollRef, onScroll } = useScrollMemory(scrollMemoryKey)
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      closeOnOverlayClick={false}
      isCentered
      blockScrollOnMount={true}
      {...modalProps}
    >
      <ModalOverlay {...modalOverlayProps} />
      <ModalContent
        position="fixed"
        top="50%"
        left="50%"
        transform="translate(-50%, -50%)"
        maxW="800px"
        minW="350px"
        w="70vw"
        borderRadius="5px"
        my={0}
        {...modalContentProps}
        className={[modalContentProps?.className, floating ? 'floating-task-modal' : ''].filter(Boolean).join(' ')}
      >
        <ModalHeader
          data-modal-part="header"
          px="20px"
          py="10px"
          zIndex={5}
          boxShadow="0 4px 6px rgba(0, 0, 0, 0.1)"
          {...modalHeaderProps}
        >
          {header}
        </ModalHeader>
        <ModalBody data-modal-part="body" px={0} py={0} overflow="hidden">
          <Flex
            flexDir="column"
            height="calc(100vh - 132px)"
            maxH="80vh"
            py={{ base: '10px', md: '15px' }}
            px={{ base: '10px', md: '25px' }}
            overflowY="auto"
            bg="#f7f7f7"
            justifyContent="space-between"
            data-scroll-container
            {...modalBodyProps}
            {...(scrollMemoryKey
              ? {
                  ref: setScrollRef,
                  onScroll: (e: React.UIEvent<HTMLDivElement>) => {
                    ;(modalBodyProps as any)?.onScroll?.(e)
                    onScroll()
                  },
                }
              : {})}
          >
            {body}
          </Flex>
        </ModalBody>
        <ModalFooter
          data-modal-part="footer"
          overflow="hidden"
          borderBottomLeftRadius="5px"
          borderBottomRightRadius="5px"
          px="10px"
          py="10px"
          {...modalFooterProps}
          zIndex={10000}
        >
          {footer}
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default BaseModalV2
